import { useState, useEffect } from 'react';
import { Link, useParams } from 'wouter';
import { fetchBlogPost, formatDate, type BlogPostDetail } from '@/lib/blog-api';
import { Clock, Tag, ArrowLeft, Calendar, Share2 } from 'lucide-react';
import { SEO } from "@/components/seo";

function getSSRPostData(): BlogPostDetail | null {
  const data = (window as any).__BLOG_DATA__;
  if (data?.type === 'post') {
    return data.post;
  }
  return null;
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const ssrPost = getSSRPostData();
  const [post, setPost] = useState<BlogPostDetail | null>(ssrPost);
  const [loading, setLoading] = useState(!ssrPost);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clean up SSR data to avoid stale state on SPA navigation
    if ((window as any).__BLOG_DATA__?.type === 'post') {
      delete (window as any).__BLOG_DATA__;
      return;
    }

    async function loadPost() {
      if (!slug) return;
      try {
        setLoading(true);
        setError(null);
        const data = await fetchBlogPost(slug);
        setPost(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load post');
      } finally {
        setLoading(false);
      }
    }
    loadPost();
  }, [slug]);

  // Build JSON-LD for blog post
  const getJsonLd = (post: BlogPostDetail) => ({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    image: post.coverImage || post.ogImage,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: {
      '@type': 'Organization',
      name: 'BounceBunny',
    },
    publisher: {
      '@type': 'Organization',
      name: 'BounceBunny',
      logo: {
        '@type': 'ImageObject',
        url: 'https://bouncebunny.app/assets/logo.png',
      },
    },
    description: post.seoDescription || post.brief,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://bouncebunny.app/blog/${post.slug}`,
    },
  });

  const handleShare = async () => {
    if (!post) return;
    const url = `https://bouncebunny.app/blog/${post.slug}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.brief,
          url,
        });
      } catch {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-pulse text-white/60">Loading post...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
            <p className="text-white/60 mb-6">{error || 'This blog post does not exist.'}</p>
            <Link href="/blog" className="text-blue-400 hover:underline">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title={`${post.seoTitle || post.title} | BounceBunny Blog`}
        description={post.seoDescription || post.brief}
        canonical={`https://bouncebunny.app/blog/${post.slug}`}
        ogImage={post.ogImage || post.coverImage || undefined}
        ogType="article"
        jsonLd={getJsonLd(post)}
      />
      {/* Hero */}
      {post.coverImage && (
        <div className="relative h-[40vh] md:h-[50vh] overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        </div>
      )}

      <article className="container mx-auto px-4 py-8 md:py-12">
        {/* Back link */}
        <Link
          href="/blog"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        {/* Header */}
        <header className="max-w-3xl mx-auto mb-8">
          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map((tag) => (
                <Link
                  key={tag.slug}
                  href={`/blog/tag/${tag.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-sm transition-colors"
                >
                  <Tag className="w-3.5 h-3.5" />
                  {tag.name}
                </Link>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(post.publishedAt)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readTimeInMinutes} min read
            </span>
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 hover:text-white transition-colors ml-auto"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-3xl mx-auto">
          <div
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-white
              prose-p:text-white/80 prose-p:leading-relaxed
              prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-code:text-blue-300 prose-code:bg-white/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-white/5 prose-pre:border prose-pre:border-white/10
              prose-blockquote:border-l-blue-400 prose-blockquote:text-white/70
              prose-img:rounded-lg
              prose-li:text-white/80"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* Footer */}
        <footer className="max-w-3xl mx-auto mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              More articles
            </Link>
            <Link
              href="/form"
              className="btn btn-primary"
            >
              Get Your Free Training Plan
            </Link>
          </div>
        </footer>
      </article>
    </div>
  );
}
